import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { GRAPHQL_URL } from './config';
import { logger } from './utils/logger';

// Create HTTP link with credentials and optimized timeout for assignments page
const httpLink = new HttpLink({
  uri: GRAPHQL_URL,
  credentials: 'include',
  fetchOptions: {
    credentials: 'include',
    // 🎯 PERFORMANCE OPTIMIZATION: Reduced timeout for assignments queries
    // Use 2 minutes for regular queries, 10 minutes for sync operations
    timeout: 2 * 60 * 1000 // 2 minutes for regular queries
  }
});

// Global logout flag to prevent queries during logout
let isLoggingOut = false;
export const setLoggingOut = (value) => {
  isLoggingOut = value;
};

// Create the auth link
const authLink = setContext((_, { headers }) => {
  // Block all requests if we're logging out
  if (isLoggingOut) {
    throw new Error('Request blocked during logout');
  }

  return {
    headers: {
      ...headers,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Apollo-Require-Preflight': 'true'
    },
    credentials: 'include'
  }
});

// Error handling link - handles authentication errors and prevents them from reaching UI
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      // Only log authentication errors if we're not already on the login page
      // This prevents spam during logout process
      const isOnLoginPage = window.location.pathname === '/login';
      const isAuthenticationError = err.extensions?.code === 'UNAUTHENTICATED' ||
        err.message.includes('No token provided') ||
        err.message.includes('Invalid token') ||
        err.message.includes('Not authenticated') ||
        err.message.includes('You must be logged in');

      if (!isOnLoginPage && !isAuthenticationError) {
        logger.error('GraphQL Error:', {
          message: err.message,
          path: err.path,
          code: err.extensions?.code
        });
      }

      // Handle authentication errors - redirect to login instead of showing errors
      // Only redirect on UNAUTHENTICATED (not logged in), NOT on FORBIDDEN (logged in but lacks permission)
      if (isAuthenticationError) {
        // Only redirect if we're not already on the login page
        if (!isOnLoginPage) {
          logger.debug('Authentication error detected, redirecting to login');
          window.location.href = '/login';
        }
        return;
      }

      // Don't handle cache read errors the same way - let components deal with them
      if (err.message.includes("Can't find field") && operation.operationName !== 'login') {
        logger.debug('Apollo cache read miss', { message: err.message, operationName: operation.operationName });
        // Don't redirect, just let the component handle the missing data
        return forward(operation);
      }
    }
  }

  if (networkError) {
    const isOnLoginPage = window.location.pathname === '/login';

    // Don't log errors if they're caused by logout blocking
    if (!isOnLoginPage && !networkError.message?.includes('Request blocked during logout')) {
      logger.error('Network Error:', {
        message: networkError.message,
        statusCode: networkError.statusCode
      });
    }

    // Handle network authentication errors - redirect to login (only 401, not 403)
    if (networkError.statusCode === 401) {
      if (!isOnLoginPage) {
        logger.debug('Network authentication error detected, redirecting to login');
        window.location.href = '/login';
      }
      return;
    }
  }
});

// Create a more forgiving cache that doesn't throw on missing fields
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        notes: {
          // Return empty array if missing
          read(existing) {
            return existing || [];
          }
        }
      }
    }
  }
});

// Create Apollo Client instance with error handling
const client = new ApolloClient({
  link: errorLink.concat(authLink.concat(httpLink)),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'ignore',
      notifyOnNetworkStatusChange: false // Reduce re-renders
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: false // Reduce re-renders
    },
    mutate: {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache'
    }
  },
  connectToDevTools: process.env.NODE_ENV === 'development',
  // Add this configuration to explicitly include credentials with EVERY request
  request: (operation) => {
    operation.setContext({
      fetchOptions: {
        credentials: 'include',
      },
    });
  },
});

export default client;
